"use client";

import { useSubscription, useSuspenseQuery } from "@apollo/client";
import { FC, PropsWithChildren, useEffect, useMemo } from "react";

import { GetProfileQuery } from "@/.gql/graphql";
import { GET_PROFILE, UPDATE_PROFILE } from "@/.graphql";
import { setCookie } from "@/app/_actions/setCookie";
import { getIsPwa } from "@/utils/getIsPwa";
import { sendGTM } from "@/utils/gtm";

import { createCustomContext } from "./context";
import { COOKIES } from ".constants/cookies";

interface ABTestData {
  ga_ab: number;
  ga_abc: number;
  segment_ab: number;
}

export type Profile = GetProfileQuery["getProfile"];

const [Context, useProfileContext] = createCustomContext<Profile>({
  name: "ProfileContext",
  strict: false,
});
export { useProfileContext };

export const ProfileProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data } = useSuspenseQuery(GET_PROFILE);
  useSubscription(UPDATE_PROFILE);

  // Мемоизируем только необходимые данные для AB-тестов
  const abTestProfile = useMemo(
    () =>
      data?.getProfile
        ? {
            id: data.getProfile.id,
            isAdmin: data.getProfile.isAdmin,
            ga_ab: data.getProfile.ga_ab,
            ga_abc: data.getProfile.ga_abc,
            segment_ab: data.getProfile.segment_ab,
          }
        : null,
    [data.getProfile],
  );

  // Мемоизируем AB-тест данные
  const abTestData = useMemo(
    () =>
      abTestProfile
        ? {
            ga_ab: abTestProfile.ga_ab,
            ga_abc: abTestProfile.ga_abc,
            segment_ab: abTestProfile.segment_ab,
          }
        : null,
    [abTestProfile],
  );

  // Эффект для AB-тестов будет срабатывать только при изменении relevant данных
  useEffect(() => {
    if (!abTestProfile?.id || !abTestData) {
      return;
    }

    sendGTM({
      event: "Match_bd",
      ecommerce: {
        usid: abTestProfile.id,
        ...abTestData,
        isPWA: getIsPwa(),
      },
    });

    Object.entries({
      [COOKIES.GA_AB]: abTestData.ga_ab,
      [COOKIES.GA_ABC]: abTestData.ga_abc,
      [COOKIES.SEGMENT_AB]: abTestData.segment_ab,
    }).forEach(([key, value]) => {
      setCookie(key, String(value));
    });

    if (abTestProfile.isAdmin) {
      console.log("[INFO] AB profile:", JSON.stringify(abTestData, null, 2));
    }
  }, [abTestProfile?.id, abTestProfile?.isAdmin, abTestData]);

  // Для контекста используем полные данные профиля
  const profile = useMemo(() => data?.getProfile, [data?.getProfile]);

  return <Context value={profile}>{children}</Context>;
};
